import { IonButton, IonIcon, IonSpinner, IonText as Text } from '@ionic/react';
import { linkOutline } from 'ionicons/icons';
import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useHistory, useLocation } from 'react-router';
import styled from 'styled-components';
import { t } from 'ttag';

import { ReactComponent as EMLeadCaptureLogoLight } from '@assets/lead-capture-app-logo.svg';
import Content from '@components/Content';
import RoutePath from '@custom-types/RoutePath';
import { VerifyType } from '@custom-types/UserSession';
import Layout from '@pages/Auth/Layout';
import { Locale } from '@services/language-service';
import { generateRoute } from '@services/navigation-service';
import { getLocale } from '@state/system';
import { setUser } from '@state/user';
import { Browser } from '~capacitor-plugins/browser';

import EmailLinkSuccess, { useSendEmailLink } from './EmailLinkSuccess';
import { ErrorCode, authenticate } from './service';

const Box = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  flex-direction: column;
`;
const Body = styled(Box)`
  flex: 1;
`;
const LoginSupport = styled(Box)`
  margin-bottom: 80px;
  flex-direction: row;
`;
const IonText = styled(Text)`
  text-align: center;
`;
const Button = styled(IonButton)`
  width: 100%;
`;
const Container = styled(Box)`
  flex: 1;
  justify-content: space-between;
  padding: 0 10px;
  margin-top: 50px;
`;

enum LoginSupportLinks {
  En = 'https://help.eventmobi.com/en/knowledge/lead-capture-app-login-support',
  De = 'https://help.eventmobi.com/de/knowledge/unterstutzung-der-lead-capture-app',
}

const VerifyEmailLink = () => {
  const { search } = useLocation();
  const searchParams = new URLSearchParams(search);
  const token = searchParams.get('token');
  const email = searchParams.get('email') ?? undefined;
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<ErrorCode>();
  const locale = useSelector(getLocale);
  const loginSupportLink = locale === Locale.De ? LoginSupportLinks.De : LoginSupportLinks.En;
  const history = useHistory();
  const dispatch = useDispatch();
  const { isResending, isEmailLinkSent, sendEmailLink } = useSendEmailLink();

  useEffect(() => {
    const effect = async () => {
      if (token && email) {
        try {
          const user = await authenticate({
            email,
            stage: VerifyType.OneTimeCode,
            oneTimeCode: token,
          });
          setIsLoading(false);
          dispatch(setUser(user));
          history.replace(generateRoute(RoutePath.EventList));
        } catch (e) {
          setIsLoading(false);
          setError((e as { type: ErrorCode }).type);
        }
      }
    };

    // skipcq: JS-0328
    effect();
  }, [token, email, dispatch, history]);

  if (isLoading) {
    return (
      <Content>
        <Body>
          <EMLeadCaptureLogoLight />
          <h3>{t`Redirecting to Lead Capture App`}</h3>
          <IonSpinner name="circular" color="primary" />
        </Body>
      </Content>
    );
  }

  return (
    <Layout hideLogo>
      <>
        {isEmailLinkSent && (
          <EmailLinkSuccess
            user={{ email }}
            // skipcq: JS-0417, JS-0336
            goBack={() => history.replace(generateRoute(RoutePath.Login))}
          />
        )}
        {!isEmailLinkSent && (
          <Container>
            <Box>
              <IonIcon
                slot="start"
                icon={linkOutline}
                style={{ fontSize: '158px', color: '#f69e1c' }}
              />
              {error === ErrorCode.InvalidCode ? (
                <IonText>
                  <h1>{t`Link Expired`}</h1>
                  <p>{t`For security reasons, login links can only be used once and expire in 24 hours or when a new link is generated. You can create a new login link using the button below.`}</p>
                </IonText>
              ) : (
                <IonText>
                  <h1>{t`Something went wrong!`}</h1>
                  <p>{t`An issue occurred while accessing the link. Please resend the link and try again. `}</p>
                </IonText>
              )}
            </Box>
            <Button
              type="button"
              role="button"
              expand="block"
              fill="outline"
              className="ion-padding-vertical"
              disabled={isResending}
              // skipcq: JS-0417, JS-0336
              onClick={() => email && sendEmailLink(email)}
            >
              {t`Resend Login Link`}
            </Button>
          </Container>
        )}
        <LoginSupport>
          <IonText>{t`Need Help?`}</IonText>
          <IonButton
            fill="clear"
            className="ion-text-capitalize"
            // skipcq: JS-0417, JS-0336
            onClick={() => Browser.open({ url: loginSupportLink })}
          >
            {t`Login Support`}
          </IonButton>
        </LoginSupport>
      </>
    </Layout>
  );
};

export default VerifyEmailLink;
